import { useState } from 'react';
import { useEnrichmentSettings, useEnrichmentQuota } from '../../../lib/enrichment/hooks';
import { EnrichmentDimensionsTab } from './EnrichmentDimensionsTab';
import { EnrichmentSourcesTab } from './EnrichmentSourcesTab';
import { EnrichmentQuotaTab } from './EnrichmentQuotaTab';
import { EnrichmentMarketplaceTab } from './EnrichmentMarketplaceTab';

type Tab = 'dimensions' | 'sources' | 'quota' | 'marketplace';

export function EnrichmentSettingsPage() {
  const { settings, loading, error, patch } = useEnrichmentSettings();
  const { quota } = useEnrichmentQuota();
  const [tab, setTab] = useState<Tab>('dimensions');
  const [toggling, setToggling] = useState(false);

  if (loading) {
    return <div className="p-8 text-[13px] text-[#787774] text-center">Caricamento…</div>;
  }
  if (error || !settings) {
    return (
      <div className="p-8 text-[13px] text-[#e03e3e] text-center">
        Errore: {error ?? 'Settings non disponibili'}
      </div>
    );
  }

  const toggleMaster = async () => {
    setToggling(true);
    await patch({ master_enabled: !settings.master_enabled });
    setToggling(false);
  };

  const masterOn = settings.master_enabled;
  const quotaUsedPct = quota?.quota.monthly_credits_quota
    ? Math.round((quota.quota.monthly_credits_used / quota.quota.monthly_credits_quota) * 100)
    : 0;

  const tabs: Array<{ id: Tab; label: string; disabled?: boolean }> = [
    { id: 'dimensions', label: 'Cosa arricchire', disabled: !masterOn },
    { id: 'sources', label: 'Da dove', disabled: !masterOn },
    { id: 'quota', label: 'Crediti & costi' },
    { id: 'marketplace', label: 'Marketplace' },
  ];

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 text-[#37352f]">
      {/* Page title */}
      <header className="mb-6">
        <h1 className="text-[24px] font-semibold mb-1">Arricchimento lead</h1>
        <p className="text-[13px] text-[#787774] max-w-2xl">
          Arricchimento automatico dei lead con dati firmografici (azienda) e personali (B2B).
          Ogni dato salvato registra origine e base legale GDPR.
        </p>
      </header>

      {/* Master switch row */}
      <div className="border border-[rgba(55,53,47,0.09)] rounded-md p-3 flex items-center gap-3 mb-4">
        <button
          onClick={toggleMaster}
          disabled={toggling}
          className={`relative inline-flex w-9 h-5 rounded-full transition-colors shrink-0 ${
            masterOn ? 'bg-[#37352f]' : 'bg-[rgba(55,53,47,0.16)]'
          } disabled:opacity-50`}
          aria-label="master toggle"
        >
          <span
            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
              masterOn ? 'translate-x-4' : ''
            }`}
          />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium">
            {masterOn ? 'Arricchimento attivo' : 'Arricchimento disattivato'}
          </div>
          <div className="text-[12px] text-[#787774]">
            {masterOn
              ? `${quota?.quota.monthly_credits_used ?? 0} / ${quota?.quota.monthly_credits_quota ?? 0} crediti usati questo mese`
              : 'Attivazione richiede consapevolezza GDPR (base legale + consenso)'}
          </div>
        </div>
        {masterOn && quotaUsedPct >= 90 && (
          <span className="text-[12px] text-[#e03e3e]">Quota al {quotaUsedPct}%</span>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-[rgba(55,53,47,0.09)] flex gap-1">
        {tabs.map((t) => {
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => !t.disabled && setTab(t.id)}
              disabled={t.disabled}
              className={`px-3 py-2 text-[13px] -mb-px border-b-2 transition-colors ${
                isActive
                  ? 'border-[#37352f] text-[#37352f] font-medium'
                  : 'border-transparent text-[#787774] hover:text-[#37352f]'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="py-6">
        {tab === 'dimensions' && <EnrichmentDimensionsTab settings={settings} onPatch={patch} />}
        {tab === 'sources' && <EnrichmentSourcesTab settings={settings} onPatch={patch} />}
        {tab === 'quota' && <EnrichmentQuotaTab quota={quota} />}
        {tab === 'marketplace' && <EnrichmentMarketplaceTab />}
      </div>
    </div>
  );
}
